import { PackagesFormValues } from '@features/DynamicPackagesConfigurationWizard/types';
import { Rate } from '@features/rates/types';
import { RoomExtra } from '@features/rooms';
import { some } from '@utils/some';
import Cookie from 'js-cookie';
import { Guest } from 'shared-types';
import { BasketDetails } from 'shared-types/Basket';
import { Property } from 'shared-types/Property';
import { appendSpaAndTableToRoomsItemInBasket } from './appendSpaAndTableToRoomsItemInBasket';
import { filterIncompleteGuests } from './guest.helpers';
import { mergeGuestData } from './mergeGuestData';
import { parseRoomDetailsForBasket } from './parseRoomDetailsForBasket';

export type MappedLoggedGuestData = Pick<
  Guest,
  'title' | 'firstName' | 'lastName' | 'gender' | 'externalSpaId'
> & {
  isLead?: boolean;
  loggedInUser?: {
    id: number;
  };
};

export type AddToBasketProps = {
  updateBasket: (...args: any[]) => Promise<{ payload: BasketDetails }>;
  createBasket: (...args: any[]) => Promise<{ payload: BasketDetails }>;
  bookingDetails: PackagesFormValues;
  roomRateEntity: Rate;
  roomId: string;
  children: number;
  childAges?: { [child: string]: number };
  fromDate: string;
  toDate: string;
  property: Property;
  language: string;
  basketDetails: any;
  showConfirmation: () => void;
  spaPackageId: number | string;
  spaOpenTime: string;
  spaCloseTime: string;
  maxGuestsPerExperience: number;
  availableExtras: RoomExtra[];
  loggedInUserGuestData?: MappedLoggedGuestData | null;
  isPackageRepeating: boolean;
  experienceGuestStartIndex?: number;
  roomNumber?: number;
  existingBasketId?: string;
  existingBasketItems?: any[];
  packageParentId?: number;
  promoRateCode?: string;
};

export async function addToBasket(props: AddToBasketProps): Promise<void> {
  const {
    updateBasket,
    createBasket,
    bookingDetails,
    roomRateEntity,
    roomId,
    children,
    childAges,
    fromDate,
    toDate,
    property,
    language,
    basketDetails,
    availableExtras,
    loggedInUserGuestData,
    experienceGuestStartIndex = 0,
    roomNumber = 0,
    existingBasketId = undefined,
    existingBasketItems = [],
    promoRateCode,
  } = props;

  const basketId = existingBasketId || Cookie.get('basketId');
  const { notes: roomNotes } = bookingDetails.guests;
  const basketItems = some(existingBasketItems) ? existingBasketItems : basketDetails?.items || [];
  const filteredGuests = filterIncompleteGuests(
    bookingDetails.guests.rooms[roomNumber],
    loggedInUserGuestData ?? null
  );
  const existingGuests = (basketItems || []).flatMap((item) => item.guestDetails);

  const mergedGuests = mergeGuestData(existingGuests, filteredGuests);

  const roomDetailsForBasket = parseRoomDetailsForBasket({
    roomRateEntity,
    guests: mergedGuests,
    roomId,
    adults: bookingDetails.guests.rooms[roomNumber].length,
    children,
    childAges,
    fromDate,
    toDate,
    note: roomNotes,
    availableExtras,
    formExtras: bookingDetails.extras,
    promoRateCode,
  });

  const items = roomDetailsForBasket;

  const response = basketId
    ? await updateBasket(
        property.id,
        basketId,
        language,
        {
          items: [...basketItems, ...items].filter((item) => !!item),
        },
        true
      )
    : await createBasket(property.id, language, { items: items.filter((item) => !!item) }, true);

  const mostRecentPackageParentId = response.payload.items[response.payload.items.length - 1].id;

  try {
    await appendSpaAndTableToRoomsItemInBasket({
      ...props,
      experienceGuestStartIndex,
      roomNumber: roomNumber,
      existingBasketId: response.payload.id,
      existingBasketItems: response.payload.items,
      packageParentId: mostRecentPackageParentId ? Number(mostRecentPackageParentId) : undefined,
    });
  } catch (error) {
    const updatedItems = response.payload.items.filter(
      (item) => item.id !== mostRecentPackageParentId
    );
    await updateBasket(
      property.id,
      response.payload.id,
      language,
      {
        items: updatedItems,
      },
      true
    );
    throw error;
  }
}
